import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'react-hot-toast';
import { AuthContext } from './AuthContext';

export const MenuPermissionsContext = createContext();

// Default permissions that every user should have
const DEFAULT_PERMISSIONS = {
  '/': { name: 'Dashboard', canAccess: true },
  '/my-dashboard': { name: 'My Dashboard', canAccess: true },
  '/my-work': { name: 'My Work', canAccess: true },
  '/clients': { name: 'Clients', canAccess: true },
  '/vendors': { name: 'Vendors', canAccess: true },
  '/projects': { name: 'Projects', canAccess: true },
  '/tasks': { name: 'Tasks', canAccess: true },
  '/company': { name: 'Company Page', canAccess: true },
  '/price-book': { name: 'Price Book', canAccess: true },
  '/products': { name: 'Products', canAccess: true },
  '/imports': { name: 'Data Imports', canAccess: true }, // Admin-only in DB but allow as fallback
  '/views': { name: 'Views', canAccess: true },
  '/audit-reporting': { name: 'Audit & Reporting', canAccess: true },
  '/security': { name: 'Security', canAccess: true }, // Admin-only in DB but allow as fallback
  '/settings': { name: 'Settings', canAccess: true }  // Admin-only in DB but allow as fallback
};

export const MenuPermissionsProvider = ({ children }) => {
  const [menuPermissions, setMenuPermissions] = useState(DEFAULT_PERMISSIONS);
  const [loading, setLoading] = useState(false); // Start with loading=false so UI shows immediately
  const { currentUser, isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  
  useEffect(() => {
    // Only fetch permissions if user is authenticated - otherwise use defaults
    if (isAuthenticated && currentUser) {
      fetchMenuPermissions();
    }
  }, [isAuthenticated, currentUser]);
  
  // Function to fetch menu permissions from the server
  const fetchMenuPermissions = async () => {
    try {
      console.log('Attempting to fetch menu permissions');
      setLoading(true);
      
      const response = await api.get('/auth/menu-permissions');
      console.log('Menu permissions response data:', response.data);
      
      // If we got permissions data, use it
      if (response.data.permissions && Object.keys(response.data.permissions).length > 0) {
        setMenuPermissions(response.data.permissions);
        console.log('Menu permissions set from API response');
      } else {
        console.log('No menu permissions in response, using defaults');
        // If no permissions returned, fall back to defaults
        setMenuPermissions(DEFAULT_PERMISSIONS);
      }
    } catch (error) {
      console.error('Failed to fetch menu permissions:', error);
      // On error, use default permissions
      setMenuPermissions(DEFAULT_PERMISSIONS);
    } finally {
      setLoading(false);
    }
  };

  // Check if a user has permission to access a menu path
  const hasMenuPermission = (menuPath) => {
    // Always allow navigation to these critical paths
    if (menuPath === '/' || menuPath === '/my-dashboard' || menuPath === '/company') {
      return true;
    }
    
    // Check if permissions exist for this path
    if (menuPermissions && menuPermissions[menuPath]) {
      return menuPermissions[menuPath].canAccess;
    }
    
    // If path isn't in permissions but is in defaults, allow access
    if (DEFAULT_PERMISSIONS[menuPath]) {
      return DEFAULT_PERMISSIONS[menuPath].canAccess;
    }
    
    // Default deny for unknown paths
    return false;
  };

  // Navigate to a menu item if permitted
  const navigateIfPermitted = (menuPath) => {
    if (hasMenuPermission(menuPath)) {
      navigate(menuPath);
    } else {
      toast.error('You do not have permission to access this page');
    }
  };

  return (
    <MenuPermissionsContext.Provider
      value={{
        menuPermissions,
        loading,
        hasMenuPermission,
        navigateIfPermitted
      }}
    >
      {children}
    </MenuPermissionsContext.Provider>
  );
};

// Custom hook to use menu permissions
export const useMenuPermissions = () => {
  const context = useContext(MenuPermissionsContext);
  if (!context) {
    throw new Error('useMenuPermissions must be used within a MenuPermissionsProvider');
  }
  return context;
};

export default MenuPermissionsContext; 